import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { mediaManagerClient } from '@/lib/axios';
import { MutationConfig, queryClient } from '@/lib/react-query';
import { useNotificationStore } from '@/stores/notifications';
import { emptyStringValues } from '@/utils/objects';

import { ChannelMediaTypeEnum, ChannelScopeEnum, ChannelType } from '../types';

import { channelsQueryKeys } from './query-keys';
import { useUpdateChannelImage } from './update-channel-image';

export type TPostChannelsParams = {
  name: string;
  media_type: ChannelMediaTypeEnum;
  scope: ChannelScopeEnum;
  av_code?: string | null;
  country_code: string;
  short_name?: string | null;
  region_code?: string | null;
  subregion_code?: string | null;
  parent_channel_id?: string | null;
  logo?: File | string | null;
  audience_channel_source_id?: string | null;
  economical_value_channel_source_id?: string | null;
};

export async function postChannel(body: TPostChannelsParams): Promise<ChannelType> {
  return mediaManagerClient.post('/api/v1/channels', emptyStringValues(body));
}

type MutationFn = typeof postChannel;

type TUsePostBroadcastersParams = {
  config?: MutationConfig<MutationFn>;
};

export function usePostChannel({ config = {} }: TUsePostBroadcastersParams = {}) {
  const { t } = useTranslation();
  const { addErrorNotification, addSuccessNotification } = useNotificationStore();
  const { mutate: updateChannelImage } = useUpdateChannelImage();

  return useMutation({
    ...config,
    mutationFn: postChannel,
    onError(error) {
      addErrorNotification(t('No se ha podido crear la emisora'));
      console.error(error);
    },
    onSuccess(channel, variables) {
      addSuccessNotification(t('La emisora se ha creado'));

      if (variables.logo instanceof File) {
        updateChannelImage({
          id: channel.id,
          logo: variables.logo,
        });
      }

      queryClient.invalidateQueries({ queryKey: channelsQueryKeys.all });
    },
  });
}
